// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { type LookAndFeelRootPropsCanceledPayment } from '@tectonics/look-and-feel';
import {
  IncomingPaymentProvider,
  useIncomingPayment,
  getCountry,
  getRedirectUrlAfterPayment,
  getMustRedirectAfterPayment,
  getWaitBeforeRedirect,
} from '@tectonics/prs/incoming-payment';
import { PaymentSpecProvider } from '@tectonics/prs/payment-spec';
import { AnalyticsContextProvider, AnalyticsCachedEvent } from '@tectonics/analytics';
import { Redirect } from '@tectonics/look-and-feel/commons/redirect';

import { LoadingWow } from '../../commons/molecules/loading';

export function CanceledPaymentPage({
  spec,
  incomingPayment,
  analytics,
}: LookAndFeelRootPropsCanceledPayment): Node {
  if (incomingPayment.status !== 'CA') {
    throw new Error(`CanceledPaymentPage: trying to render "${incomingPayment.status}" payment.`);
  }

  const redirectUrl = getRedirectUrlAfterPayment(incomingPayment);
  const mustRedirect = getMustRedirectAfterPayment(incomingPayment);
  const waitBeforeRedirect = getWaitBeforeRedirect(incomingPayment) || 2000;

  return (
    <PaymentSpecProvider spec={spec}>
      <IncomingPaymentProvider incomingPayment={incomingPayment}>
        <AnalyticsContextProvider analytics={analytics}>
          {mustRedirect && redirectUrl && <Redirect url={redirectUrl} delay={waitBeforeRedirect} />}
          <AnalyticsCachedEvent eventType="pageView" />
          <Page redirecting={Boolean(redirectUrl && mustRedirect)} />
        </AnalyticsContextProvider>
      </IncomingPaymentProvider>
    </PaymentSpecProvider>
  );
}

type PageProps = {|
  redirecting: boolean,
|};

function Page({ redirecting }: PageProps): Node {
  return (
    <CardCanceledWrapper data-test-id="page-canceled-payment">
      {redirecting && <LoadingWow />}
      <CanceledPageMessage redirecting={redirecting} />
    </CardCanceledWrapper>
  );
}

function CanceledPageMessage({ redirecting }: PageProps): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch(country){
    case 'br':
      return (
        <>
          <PaymentCanceledMessage>Esse pagamento foi cancelado :(</PaymentCanceledMessage>
          {redirecting && <MessageBody> Aguarde enquanto te redirecionamos para o site em que a compra foi feita.</MessageBody>}
        </>
      );
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return (
        <>
          <PaymentCanceledMessage>Este pago fue cancelado :(</PaymentCanceledMessage>
          {redirecting && <MessageBody> Espera mientras te redirecionamos al sitio donde la compra fue hecha.</MessageBody>}
        </>
      );
    case 'za':
    case 'ke':
    case 'ng':
      return (
        <>
          <PaymentCanceledMessage>This payment has been cancelled :(</PaymentCanceledMessage>
          {redirecting && <MessageBody> Please wait while we redirect you to the site where the purchase was made.</MessageBody>}
        </>
      );

    default:
      throw new Error(`Canceled page translation not found for country: ${country}`);
  }
}

const PaymentCanceledMessage = styled.div`
  margin-top: 40px;
  color: #2397d4;
  font-size: 22px;
  font-weight: 400;
  font-family: ${props => props.theme.fontFamily.display};
`;

const MessageBody = styled.div`
  margin-top: 5px;
  color: #5c5c5c;
  font-size: 14px;
  font-weight: 400;
  font-family: ${props => props.theme.fontFamily.sansSerif};
`;

export const CardCanceledWrapper = styled.div`
  flex: 1 1 auto;
  text-align: center;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  height: 100%;
  justify-content: center;
`;
